import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Headset, X, Send, Loader2, ImagePlus, Sparkles } from "lucide-react";
import api, { extractErr } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import AIStoreManagerDock from "@/components/AIStoreManagerDock";

const ALLOWED = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp", "image/avif"]);
const MAX_BYTES = 2 * 1024 * 1024;

function welcomeKey(tenantId) {
  return `ms_support_ai_welcome:${tenantId || "store"}`;
}

function ReplyBody({ text }) {
  if (!text) return null;
  const chunks = [];
  const re = /(\[\[OPEN_SUPPORT\]\])|(https?:\/\/[^\s]+)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) chunks.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    if (m[1]) {
      chunks.push(
        <Link key={key++} to="/admin/support" className="inline-flex font-semibold text-sky-700 underline underline-offset-2" data-testid="support-ai-open-support">
          Open chat support
        </Link>
      );
    } else {
      const href = m[2].replace(/[),.;]+$/, "");
      chunks.push(<a key={key++} href={href} target="_blank" rel="noreferrer" className="underline text-sky-700 break-all">{href}</a>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) chunks.push(<span key={key++}>{text.slice(last)}</span>);
  return <>{chunks}</>;
}

export default function MerchantSupportAi() {
  const { tenant } = useMerchantAuth();
  const [manager, setManager] = useState(null);
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileErr, setFileErr] = useState("");
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/merchant/ai-store-manager/status").then(({ data }) => {
      if (cancelled) return;
      setManager(data);
      if (data?.on) return;
      api.get("/merchant/support-ai/status").then(({ data: d }) => {
        if (cancelled) return;
        setStatus(d);
        const key = welcomeKey(tenant?.id);
        const seen = localStorage.getItem(key);
        if (d?.configured && !d.disabled && d.welcome_popup && !seen) {
          setOpen(true);
          localStorage.setItem(key, "1");
        }
      }).catch(() => { if (!cancelled) setStatus({ configured: false }); });
    }).catch(() => {
      if (cancelled) return;
      setManager({ on: false });
      api.get("/merchant/support-ai/status").then(({ data: d }) => {
        if (!cancelled) setStatus(d);
      }).catch(() => { if (!cancelled) setStatus({ configured: false }); });
    });
    return () => { cancelled = true; };
  }, [tenant?.id]);

  useEffect(() => {
    if (!status) return;
    if (messages.length) return;
    const name = tenant?.name ? ` ${tenant.name}` : "";
    setMessages([{
      role: "assistant",
      text: status.disabled
        ? "AI support is paused for review. Please use Open chat support with the team. [[OPEN_SUPPORT]]"
        : (status.welcome_message || `Welcome${name}! Watch this intro: https://youtu.be/2Tcgujq6Uwk\n\nThen ask me anything about your dashboard — products, payments, ads, or App Store addons. I will reply in your language.`),
    }]);
  }, [status, tenant?.name, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  const addFiles = (list) => {
    setFileErr("");
    const incoming = Array.from(list || []);
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= 2) { setFileErr("Maximum 2 screenshots."); break; }
      const type = (f.type || "").toLowerCase();
      if (!ALLOWED.has(type) && !/\.(jpe?g|png|webp)$/i.test(f.name || "")) {
        setFileErr("Use JPEG, PNG or WebP.");
        continue;
      }
      if (f.size > MAX_BYTES) { setFileErr("Each screenshot must be 2MB or smaller."); continue; }
      next.push({ file: f, url: URL.createObjectURL(f) });
    }
    setFiles(next.slice(0, 2));
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (busy || status?.disabled) return;
    if (!text && files.length === 0) return;
    setInput("");
    const attached = files.map((x) => x.file);
    const thumbs = files.map((x) => x.url);
    setFiles([]);
    setMessages((m) => [...m, { role: "user", text: text || "Please look at my screenshot.", thumbs }]);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("message", text);
      attached.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/merchant/support-ai/message", fd);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      const code = err?.response?.status;
      const msg = extractErr(err) || "The assistant is busy. Please try again.";
      if (code === 403) {
        setStatus((s) => ({ ...s, disabled: true }));
        setMessages((m) => [...m, { role: "assistant", text: `${msg} [[OPEN_SUPPORT]]` }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", text: msg }]);
      }
    } finally {
      setBusy(false);
    }
  };

  if (manager?.on) return <AIStoreManagerDock status={manager} />;
  if (!status?.configured || status.platform_enabled === false) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="support-ai-fab"
          className="fixed bottom-5 right-5 z-40 group"
          aria-label="Mera Startup Support"
        >
          <span className="absolute -top-10 right-0 whitespace-nowrap rounded-full bg-slate-900 text-white text-[11px] font-semibold px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            Mera Startup Support
          </span>
          <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 via-indigo-500 to-violet-600 text-white shadow-[0_12px_30px_-8px_rgba(79,70,229,0.7)] ring-2 ring-white hover:scale-105 transition-transform">
            <Headset className="w-7 h-7" />
            <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-white" />
          </span>
        </button>
      )}

      {open && (
        <div
          data-testid="support-ai-panel"
          className="fixed z-50 flex flex-col overflow-hidden bg-white shadow-2xl border border-slate-200/80
            inset-x-3 bottom-3 top-16 rounded-2xl
            sm:inset-auto sm:bottom-5 sm:right-5 sm:top-auto sm:w-[400px] sm:h-[580px] sm:max-h-[82vh]"
        >
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-slate-900 via-indigo-950 to-violet-950 text-white">
            <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-sky-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold leading-tight" data-testid="support-ai-title">Mera Startup Support</div>
              <div className="text-[11px] text-emerald-300 flex items-center gap-1.5 mt-0.5">
                <span className={`w-1.5 h-1.5 rounded-full ${status.disabled ? "bg-amber-400" : "bg-emerald-400"}`} />
                {status.disabled ? "Paused — human support available" : "Here to set up your store"}
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} data-testid="support-ai-close" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-slate-50 to-indigo-50/40">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-indigo-600 text-white rounded-br-md"
                    : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
                }`}>
                  <ReplyBody text={m.text} />
                  {m.thumbs?.length > 0 && (
                    <div className="mt-2 flex gap-1.5 flex-wrap">
                      {m.thumbs.map((src, ti) => (
                        <img key={ti} src={src} alt="" className="h-14 w-14 rounded-lg object-cover border border-white/30" />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-500" /> Thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t border-slate-200 bg-white space-y-2">
            {files.length > 0 && (
              <div className="flex gap-2">
                {files.map((item, i) => (
                  <div key={i} className="relative">
                    <img src={item.url} alt="" className="h-12 w-12 rounded-lg object-cover border border-slate-200" />
                    <button type="button" className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-slate-900 text-white text-[10px] leading-none" onClick={() => setFiles(files.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
              </div>
            )}
            {fileErr && <div className="text-[11px] text-red-600">{fileErr}</div>}
            <div className="flex items-end gap-2">
              <button
                type="button"
                disabled={status.disabled || files.length >= 2}
                onClick={() => fileRef.current?.click()}
                data-testid="support-ai-attach"
                className="w-10 h-10 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-40"
                title="Attach screenshot (max 2, 2MB)"
              >
                <ImagePlus className="w-4 h-4" />
              </button>
              <input
                ref={fileRef}
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png,image/webp,image/avif"
                multiple
                data-testid="support-ai-file"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={status.disabled}
                data-testid="support-ai-input"
                placeholder={status.disabled ? "AI paused — open chat support" : "Ask how to add a product…"}
                className="flex-1 h-10 px-3 rounded-full border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:bg-slate-50"
              />
              <button type="submit" disabled={busy || status.disabled || (!input.trim() && files.length === 0)} data-testid="support-ai-send" className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 disabled:opacity-50">
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-slate-400 text-center">Guides only · screenshots JPEG/PNG/WebP · max 2 × 2MB</div>
          </form>
        </div>
      )}
    </>
  );
}
